import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ensureAuth } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { isSubcontractorRole } from '../utils/userRoles';
import Login from './Login';
import { getPublicSettings, type AppSettings } from '../utils/appSettings';
import type { TradeBriefingContent, TradeBriefingTradeType } from '../types';
import TradeBriefingChrome, {
  useResolvedPortalBrandingLogo,
} from '../components/tradeBriefing/TradeBriefingChrome';
import TradeBriefingDeliverablesSection from '../components/tradeBriefing/TradeBriefingDeliverablesSection';
import TradeBriefingQuoteUpload from '../components/tradeBriefing/TradeBriefingQuoteUpload';
import TradeBriefingTimberList from '../components/tradeBriefing/TradeBriefingTimberList';
import './TradeBriefingPage.css';

const TRADE_LABELS: Record<TradeBriefingTradeType, string> = {
  finishing: 'Finishing',
  millwork: 'Millwork',
  electrical: 'Electrical',
  roofing: 'Roofing',
  general: 'General',
  other: 'Other',
};

type LoadState = 'loading' | 'pin' | 'login' | 'ready' | 'denied' | 'error';

function briefingPinStorageKey(token: string): string {
  return `briefing_pin_${token}`;
}

const TradeBriefingPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { user, isAuthenticated, logout } = useAuth();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [content, setContent] = useState<TradeBriefingContent | null>(null);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState<string | null>(null);
  const [verifyingPin, setVerifyingPin] = useState(false);
  const pinInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const fetchContent = useCallback(
    async (pin?: string) => {
      const fns = getFunctions(undefined, 'us-central1');
      const fn = httpsCallable<
        { token: string; pin?: string; userAgent?: string },
        TradeBriefingContent
      >(fns, 'getTradeBriefingContent');
      return fn({
        token,
        pin: pin || undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
    },
    [token],
  );

  const loadBriefing = useCallback(
    async (pin?: string): Promise<boolean> => {
      if (!token) {
        setLoadState('denied');
        setErrorMessage('This briefing link is not valid.');
        return false;
      }
      setLoadState('loading');
      setErrorMessage(null);
      try {
        const hasNamedAccount = Boolean(
          isAuthenticated && user?.id && user.role && user.role !== 'client',
        );
        let result;
        try {
          result = await fetchContent(pin);
        } catch (firstErr: unknown) {
          const firstCode =
            firstErr && typeof firstErr === 'object' && 'code' in firstErr
              ? String((firstErr as { code?: string }).code)
              : '';
          if (!hasNamedAccount && firstCode.includes('unauthenticated')) {
            await ensureAuth();
            result = await fetchContent(pin);
          } else {
            throw firstErr;
          }
        }
        if (pin) {
          sessionStorage.setItem(briefingPinStorageKey(token), pin);
        }
        setContent({
          ...result.data,
          releaseBundles: result.data.releaseBundles ?? [],
        });
        setLoadState('ready');
        return true;
      } catch (err: unknown) {
        const code =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code?: string }).code)
            : '';
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : '';

        if (code.includes('failed-precondition') && message.includes('login-required')) {
          setLoadState('login');
          return false;
        }
        if (code.includes('failed-precondition') && message.includes('pin-required')) {
          setLoadState('pin');
          return false;
        }
        if (code.includes('permission-denied')) {
          if (message.includes('Invalid access')) {
            setLoadState('pin');
            setPinError('Incorrect access code. Please try again.');
          } else {
            setLoadState('denied');
            setErrorMessage(message || 'This briefing link is no longer available.');
          }
          return false;
        }
        if (code.includes('not-found')) {
          setLoadState('denied');
          setErrorMessage('This briefing link is not valid.');
          return false;
        }
        console.error('[TradeBriefing]', err);
        setLoadState('error');
        setErrorMessage('Could not load briefing. Please try again later.');
        return false;
      }
    },
    [fetchContent, token, isAuthenticated, user?.id, user?.role],
  );

  useEffect(() => {
    getPublicSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    const storedPin = sessionStorage.getItem(briefingPinStorageKey(token)) || undefined;
    void loadBriefing(storedPin);
  }, [loadBriefing, token]);

  useEffect(() => {
    if (loadState === 'login' && isAuthenticated && user?.role && user.role !== 'client') {
      void loadBriefing();
    }
  }, [loadState, isAuthenticated, user?.role, loadBriefing]);

  const handlePinChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...pinDigits];
    next[index] = value;
    setPinDigits(next);
    setPinError(null);
    if (value && index < 3) pinInputRefs.current[index + 1]?.focus();
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (pinDigits.every(d => d !== '')) void submitPin();
    }
  };

  const handlePinPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setPinDigits(pasted.split(''));
      pinInputRefs.current[3]?.focus();
    }
  };

  const submitPin = async () => {
    const entered = pinDigits.join('');
    if (entered.length !== 4) {
      setPinError('Please enter all 4 digits');
      return;
    }
    setVerifyingPin(true);
    setPinError(null);
    const ok = await loadBriefing(entered);
    setVerifyingPin(false);
    if (!ok) {
      setPinDigits(['', '', '', '']);
      pinInputRefs.current[0]?.focus();
    }
  };

  const engagementLogo = settings?.engagementLogo ?? null;
  const engagementBackground = settings?.engagementBackground ?? null;
  const portalBg = settings?.portalBackgroundImage ?? null;
  const { src: brandingLogoSrc, onImgError: onBrandingLogoError } =
    useResolvedPortalBrandingLogo(
      content?.branding?.logoUrl
        ? { url: content.branding.logoUrl, name: 'Company logo' }
        : engagementLogo,
    );

  const resolvedBgImage =
    content?.branding?.backgroundUrl || portalBg?.url || engagementBackground?.url || null;

  const projectTitle = useMemo(() => {
    if (!content) return 'Trade Briefing';
    const num = content.projectNumber?.trim().replace(/^#+/, '');
    const descriptor = content.descriptor?.trim() || content.projectName?.trim();
    if (num && descriptor) return `#${num} ${descriptor}`;
    if (num) return `#${num}`;
    return descriptor || 'Project briefing';
  }, [content]);

  const tradeLabel = content ? TRADE_LABELS[content.tradeType] || content.tradeType : 'Trade';
  const siteAddress = content?.siteAddress?.trim() || '';
  const partyFields = content
    ? [
        { label: 'Company', value: content.companyName?.trim() || '' },
        { label: 'Contact', value: content.contactName?.trim() || '' },
        { label: 'Work', value: content.displayName?.trim() || '' },
        { label: 'Trade', value: tradeLabel },
      ].filter(field => field.value)
    : [];

  const chrome = {
    backgroundUrl: resolvedBgImage,
    logoSrc: brandingLogoSrc,
    onLogoError: onBrandingLogoError,
  };

  if (loadState === 'loading') {
    return (
      <TradeBriefingChrome {...chrome}>
        <div className="tb-gate">
          <Loader2 size={28} className="tb-spin" />
          <p>Loading briefing…</p>
        </div>
      </TradeBriefingChrome>
    );
  }

  if (loadState === 'login') {
    return <Login />;
  }

  if (loadState === 'pin') {
    return (
      <TradeBriefingChrome {...chrome}>
        <div className="tb-gate">
          <p className="tb-kicker">Trade briefing</p>
          <h1>Access code</h1>
          <p>Enter the four-digit code sent with your link.</p>
          <div className="tb-pin-inputs" onPaste={handlePinPaste}>
            {pinDigits.map((digit, index) => (
              <input
                key={index}
                ref={el => {
                  pinInputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handlePinChange(index, e.target.value)}
                onKeyDown={e => handlePinKeyDown(index, e)}
                className={`tb-pin-input${digit ? ' is-filled' : ''}`}
                autoFocus={index === 0}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>
          {pinError ? <p className="tb-pin-error">{pinError}</p> : null}
          <button
            type="button"
            className="tb-btn"
            onClick={() => void submitPin()}
            disabled={verifyingPin || pinDigits.some(d => !d)}
          >
            {verifyingPin ? <Loader2 className="tb-spin" size={14} aria-hidden /> : null}
            {verifyingPin ? 'Verifying' : 'Continue'}
          </button>
        </div>
      </TradeBriefingChrome>
    );
  }

  if (loadState === 'denied' || loadState === 'error') {
    return (
      <TradeBriefingChrome {...chrome}>
        <div className="tb-gate">
          <p className="tb-kicker">Trade briefing</p>
          <h1>Access unavailable</h1>
          <p>{errorMessage || 'This briefing link is no longer available.'}</p>
        </div>
      </TradeBriefingChrome>
    );
  }

  if (!content) return null;

  const showTimber = content.showTimberList !== false;
  const requestNotes = content.briefingNotes?.trim() || '';
  const namedSession = Boolean(isAuthenticated && user?.email && user.role && user.role !== 'client');

  const handleLogout = async () => {
    sessionStorage.removeItem(briefingPinStorageKey(token));
    await logout();
  };

  return (
    <TradeBriefingChrome {...chrome}>
      <article className="tb-sheet">
        {namedSession ? (
          <div className="tb-session">
            <p>
              Signed in as <strong>{user?.name || user?.email}</strong>
              {content.companyName ? ` · ${content.companyName}` : ''}
            </p>
            <div className="tb-session-actions">
              {isSubcontractorRole(user?.role) ? (
                <Link to="/briefings" className="tb-text-btn">
                  All projects
                </Link>
              ) : null}
              <button type="button" className="tb-text-btn" onClick={() => void handleLogout()}>
                Sign out
              </button>
            </div>
          </div>
        ) : null}

        <header className="tb-masthead">
          <p className="tb-kicker">Trade briefing</p>
          <h1>{projectTitle}</h1>
          {siteAddress ? <p className="tb-address">{siteAddress}</p> : null}
          {partyFields.length ? (
            <dl className="tb-party">
              {partyFields.map(field => (
                <div key={field.label}>
                  <dt>{field.label}</dt>
                  <dd>{field.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {content.partner?.logoUrl ? (
            <div className="tb-partner">
              <img src={content.partner.logoUrl} alt="" />
              <div className="tb-partner-copy">
                <p className="tb-kicker">In partnership with</p>
                <p className="tb-partner-name">{content.partner.companyName || content.companyName}</p>
              </div>
            </div>
          ) : null}
        </header>

        <section className="tb-section tb-request" aria-labelledby="tb-request-heading">
          <p className="tb-kicker">Request</p>
          <h2 id="tb-request-heading">What we need from you</h2>
          <blockquote className="tb-request-quote">
            <p>
              {requestNotes ||
                'Review the drawings, models, and timber list. Send a quote below if this briefing asks for one.'}
            </p>
            <footer>— LCTF Team</footer>
          </blockquote>
        </section>

        {content.confidentialBanner ? (
          <p className="tb-confidential" role="alert">
            {content.confidentialBanner}
          </p>
        ) : null}

        <TradeBriefingDeliverablesSection content={content} dealId={content.dealId} />

        {showTimber ? (
          <TradeBriefingTimberList
            structures={content.structures}
            projectTitle={projectTitle}
            companyName={content.companyName}
            tradeLabel={tradeLabel}
          />
        ) : null}

        {content.includeQuoteUpload ? (
          <TradeBriefingQuoteUpload
            token={token}
            pin={sessionStorage.getItem(briefingPinStorageKey(token)) || undefined}
            companyName={content.companyName}
            defaultLabel={
              content.displayName?.trim() || `${tradeLabel} quote`
            }
          />
        ) : null}

        <footer className="tb-footer">
          <p>Leiper&apos;s Creek Timber Frames</p>
        </footer>
      </article>
    </TradeBriefingChrome>
  );
};

export default TradeBriefingPage;
